import React, { memo, useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "antd";
import { LeftOutlined, RightOutlined, DownloadOutlined } from "@ant-design/icons";
import "./DocumentViewerModal.css";
import Button from "../buttons/Button";

const DOCUMENT_TYPES = {
  IMAGE: "image",
  PDF: "pdf",
};

function detectDocumentType(url = "") {
  if (!url) return null;
  if (url.match(/\.(pdf)$/i) || url.startsWith("data:application/pdf;base64,")) {
    return DOCUMENT_TYPES.PDF;
  }
  if (url.match(/\.(png|jpg|jpeg|webp|gif)$/i) || url.startsWith("data:image/")) {
    return DOCUMENT_TYPES.IMAGE;
  }
  return null;
}

function normalizeUrl(input) {
  if (Array.isArray(input)) {
    return input.filter((u) => u != null && u !== "");
  }
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      if (Array.isArray(parsed)) return parsed.filter((u) => u != null && u !== "");
    } catch (_) {}
    return input.trim() ? [input] : [];
  }
  return [];
}
function DocumentSkeleton() {
  return (
    <div role="status" className="document-viewer-skeleton" aria-label="Loading document">
      <div className="document-viewer-skeleton__inner">
        <svg
          className="document-viewer-skeleton__icon"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke="gray"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m3 16 5-7 6 6.5m6.5 2.5L16 13l-4.286 6M14 10h.01M4 19h16a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Z"
          />
        </svg>
      </div>
    </div>
  );
}

function DocumentRenderer({ url }) {
  const type = detectDocumentType(url);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [url]);

  if (!url) return null;

  const handleLoad = useCallback(() => setLoaded(true), []);
  const handleError = useCallback(() => setLoaded(true), []);

  if (type === DOCUMENT_TYPES.PDF) {
    return (
      <div
        className="flex items-center justify-center relative w-full h-full min-h-0"
        style={{ overflow: "auto" }}
      >
        {!loaded && <DocumentSkeleton />}
        <iframe
          src={url}
          title="PDF Preview"
          className="w-full rounded-md"
          style={{
            height: "100%",
            minHeight: "60vh",
            border: "none",
            opacity: loaded ? 1 : 0,
            position: loaded ? "relative" : "absolute",
            pointerEvents: loaded ? "auto" : "none",
          }}
          onLoad={handleLoad}
        />
      </div>
    );
  }

  if (type === DOCUMENT_TYPES.IMAGE) {
    return (
      <div className="flex justify-center items-center w-full min-h-0 relative">
        {!loaded && <DocumentSkeleton />}
        <img
          src={url}
          alt="Document"
          className="max-w-full w-auto h-auto object-contain rounded-md document-viewer-image"
          style={{
            opacity: loaded ? 1 : 0,
            position: loaded ? "relative" : "absolute",
            pointerEvents: loaded ? "auto" : "none",
          }}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }

  return <div className="text-center py-8 text-gray-500">Unsupported document type</div>;
}

function DocumentViewerModal({
  open,
  title,
  url,
  download,
  documentName,
  onReupload,
  onClose,
  reuploadButtonText = "Add",
}) {
  const [urls, setUrls] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const onReuploadRef = useRef(onReupload);

  useEffect(() => {
    if (open) {
      onReuploadRef.current = onReupload;
    }
  }, [open, onReupload]);

  useEffect(() => {
    if (open && url != null) {
      const list = normalizeUrl(url);
      setUrls(list);
      setCurrentIndex(0);
    }
  }, [open, url]);

  const currentUrl = urls[currentIndex] || null;

  const handlePrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + urls.length) % urls.length);
  }, [urls.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % urls.length);
  }, [urls.length]);

  const handleReupload = useCallback(() => {
    if (typeof onReuploadRef.current === "function") {
      onReuploadRef.current();
    }
    onClose();
  }, [onClose]);

  const handleDownload = useCallback(async () => {
    if (!currentUrl) return;
    setDownloadLoading(true);
    try {
      const response = await fetch(currentUrl);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = documentName || title || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      const fallback = document.createElement("a");
      fallback.href = currentUrl;
      fallback.target = "_blank";
      fallback.rel = "noopener noreferrer";
      fallback.download = documentName || title || "document";
      document.body.appendChild(fallback);
      fallback.click();
      document.body.removeChild(fallback);
    } finally {
      setDownloadLoading(false);
    }
  }, [currentUrl, documentName, title]);

  if (!open) return null;

  const hasMultiple = urls.length > 1;

  return (
    <Modal
      open={open}
      title={<div className="document-viewer-modal__title">{title || "Document Preview"}</div>}
      onCancel={onClose}
      destroyOnClose
      centered
      width="100%"
      className="document-viewer-modal"
      motionName="document-viewer-zoom"
      maskMotionName="document-viewer-mask"
      maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      footer={null}
      closable
    >
      <div className="document-viewer-modal__body flex flex-col px-3  sm:px-4  gap-1 sm:gap-2">
        <div className="min-h-0 flex flex-col items-stretch rounded-md overflow-hidden max-h-[min(80vh,720px)]">
          {urls.length > 0 ? (
            <div className="min-h-0 relative w-full flex justify-center items-start">
              <DocumentRenderer url={currentUrl} />
            </div>
          ) : (
            <p className="py-8 text-center text-gray-500 flex-1 flex items-center justify-center text-sm sm:text-base">
              No document to display
            </p>
          )}
        </div>

        {urls.length > 0 && hasMultiple && (
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 shrink-0 py-1.5 sm:py-2">
            <button
              type="button"
              className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white hover:opacity-90 transition-opacity touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--blackthorn-berry, #886dff)" }}
              onClick={handlePrev}
              aria-label="Previous"
            >
              <LeftOutlined className="document-viewer-nav-icon" />
            </button>
            <p className="text-gray-600 text-xs sm:text-sm font-medium min-w-[3rem] sm:min-w-[4rem] text-center">
              {currentIndex + 1} / {urls.length}
            </p>
            <button
              type="button"
              className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white hover:opacity-90 transition-opacity touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "var(--blackthorn-berry, #886dff)" }}
              onClick={handleNext}
              aria-label="Next"
            >
              <RightOutlined className="document-viewer-nav-icon" />
            </button>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-2 shrink-0">
          {(typeof onReupload === "function" || typeof onReuploadRef.current === "function") && (
            <Button variant="save" onClick={handleReupload}>
              {reuploadButtonText}
            </Button>
          )}
          {download && currentUrl && (
            <Button
              variant="saveOutline"
              loading={downloadLoading}
              onClick={handleDownload}
              leftIcon={<DownloadOutlined />}
            >
              Download
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default memo(DocumentViewerModal);
